import { IoFileTrayOutline } from 'react-icons/io5';

import { Anchor, Box, Group, ScrollArea, Stack, Text } from '@mantine/core';

import { NotificationHelpers } from 'helpers/NotificationHelpers';

import { EmptyMessage } from 'components/empty-message/EmptyMessage';
import { useInbox, useInboxFilterCounts, useInboxNotifications } from 'hooks/use-inbox';
import { NotificationService } from 'services/NotificationService';

import { InboxLoadMore } from './InboxLoadMore';
import { Message } from './Message';
import { Notification } from './Notification';
import { NotificationsSkeleton } from './NotificationsSkeleton';
import { NotificationToolbar } from './NotificationToolbar';

const Inbox = ({ maxHeight, fillHeight }: { maxHeight?: number; fillHeight?: boolean }) => {
    const { notifications, isLoading, isPreview, hasMore, isLoadingMore, loadMore } = useInboxNotifications();

    const list = (
        <>
            {notifications.map((notification) =>
                NotificationHelpers.isMessage(notification) ? (
                    <Message key={notification.id} notification={notification} />
                ) : (
                    <Notification key={notification.id} notification={notification} />
                ),
            )}
            {isPreview ? (
                <NotificationsSkeleton nbRows={1} p="sm" />
            ) : (
                <InboxLoadMore hasMore={hasMore} isLoadingMore={isLoadingMore} onLoadMore={loadMore} />
            )}
        </>
    );

    const scrollAreaProps = {
        scrollbarSize: 8,
        scrollbars: 'y',
        offsetScrollbars: true,
        styles: {
            viewport: fillHeight || maxHeight ? { overscrollBehavior: 'contain' } : undefined,
            content: { minWidth: 0 },
        },
    } as const;

    return (
        <Stack gap="md" h={fillHeight ? '100%' : undefined}>
            <NotificationToolbar />

            {isLoading ? (
                <NotificationsSkeleton />
            ) : !notifications.length && !isPreview ? (
                <InboxEmpty />
            ) : fillHeight ? (
                <ScrollArea flex={1} mih={0} {...scrollAreaProps}>
                    {list}
                </ScrollArea>
            ) : (
                <ScrollArea.Autosize mah={maxHeight} {...scrollAreaProps}>
                    {list}
                </ScrollArea.Autosize>
            )}
        </Stack>
    );
};

const InboxEmpty = () => {
    const { isFiltered, archived } = useInbox();
    const { categoryCounts } = useInboxFilterCounts();

    if (archived && categoryCounts.all.total === 0) {
        return (
            <Group gap="xs">
                <Text size="sm" c="dimmed">
                    Nothing archived yet.
                </Text>
                <Anchor component="button" fz="sm" onClick={() => NotificationService.resetFilters()}>
                    Back to inbox
                </Anchor>
            </Group>
        );
    }

    if (categoryCounts.all.total > 0) {
        return isFiltered ? (
            <Group gap="xs">
                <Text size="sm" c="dimmed">
                    Nothing here for these filters.
                </Text>
                <Anchor component="button" fz="sm" onClick={() => NotificationService.resetFilters()}>
                    Reset filters
                </Anchor>
            </Group>
        ) : (
            <Text size="sm" c="dimmed">
                You're all caught up.
            </Text>
        );
    }

    return (
        <EmptyMessage>
            <Box c="gray.3">
                <IoFileTrayOutline size={50} />
            </Box>
            <Text inherit>There are no notifications yet</Text>
            <Text inherit>Collaborate with your team and get the latest updates here</Text>
        </EmptyMessage>
    );
};

export { Inbox };
